import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { http, fmtGBP, fmtDate } from "@/lib/api";
import { ArrowLeft, MapPin, Storefront, Plus, Wrench, Certificate } from "@phosphor-icons/react";
import { StatusBadge, PriorityBadge } from "@/components/StatusBadge";
import { PhotoTile } from "@/components/PhotoTile";
import { ComplianceStatusBadge, ComplianceModal } from "@/pages/Compliance";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { formatErr, fmtDateShort } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Pencil, Trash, FilePdf } from "@phosphor-icons/react";

const EQ_STATUS_CLS = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-300",
  under_repair: "bg-amber-50 text-amber-700 border-amber-300",
  retired: "bg-slate-100 text-slate-600 border-slate-300",
};

export default function StoreDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: stores = [] } = useQuery({ queryKey: ["stores"], queryFn: () => http.get("/stores").then(r => r.data) });
  const { data: equipment = [] } = useQuery({ queryKey: ["equipment", id], queryFn: () => http.get("/equipment", { params: { store_id: id } }).then(r => r.data) });
  const { data: jobs = [] } = useQuery({ queryKey: ["jobs", "store", id], queryFn: () => http.get("/jobs", { params: { store_id: id } }).then(r => r.data) });
  const { data: brands = [] } = useQuery({ queryKey: ["brands"], queryFn: () => http.get("/brands").then(r => r.data) });
  const { data: docTypes = {} } = useQuery({ queryKey: ["compliance-doc-types"], queryFn: () => http.get("/compliance/doc-types").then(r => r.data) });
  const { data: storeDocs = [] } = useQuery({ queryKey: ["compliance", "store", id], queryFn: () => http.get("/compliance", { params: { entity_type: "store", entity_id: id } }).then(r => r.data) });

  const [complianceModal, setComplianceModal] = useState(null);
  const canManageCompliance = ["admin", "general_manager", "maintenance_manager", "district_manager", "area_manager", "store_manager"].includes(user?.role);
  const canDeleteCompliance = ["admin", "general_manager", "maintenance_manager"].includes(user?.role);

  const removeCompliance = async (docId, label) => {
    if (!window.confirm(`Soft-delete "${label}"?`)) return;
    try { await http.delete(`/compliance/${docId}`); qc.invalidateQueries({ queryKey: ["compliance", "store", id] }); }
    catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };
  const viewFile = async (fid) => {
    try {
      const r = await http.get(`/files/${fid}`, { responseType: "blob", validateStatus: () => true });
      if (r.status < 200 || r.status >= 300) { alert("File missing or expired."); return; }
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { alert("Could not open the file. Please try again."); }
  };

  const store = stores.find(s => s.id === id);
  const brand = store ? brands.find(b => b.id === store.brand_id) : null;

  if (!store) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  const openJobs = jobs.filter(j => !["closed"].includes(j.status));
  const underRepair = equipment.filter(e => e.status === "under_repair").length;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <button onClick={() => navigate(-1)} data-testid="store-detail-back" className="text-xs text-slate-500 hover:text-slate-900 mb-3 inline-flex items-center gap-1"><ArrowLeft size={12} /> Back</button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="overline">{brand?.name || "Store"}</div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1" data-testid="store-detail-name">{store.name}</h1>
          <div className="flex items-center gap-2 text-sm text-slate-600 mt-1"><MapPin size={12} />{store.address || "—"}</div>
          <div className="text-xs text-slate-500 mt-0.5">{store.district} · {store.area}</div>
        </div>
        <div className="flex gap-2">
          <Link to={`/equipment?store_id=${store.id}`} data-testid="store-detail-manage-equipment"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm border border-slate-200 hover:bg-slate-50">
            <Wrench size={14} weight="bold" /> Manage equipment
          </Link>
          <Link to={`/jobs/new?store_id=${store.id}`} data-testid="store-detail-new-job"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
            <Plus size={14} weight="bold" /> Raise job
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="rmms-card p-4"><div className="overline">Equipment</div><div className="font-mono-data text-2xl mt-2 tabular-nums">{equipment.length}</div></div>
        <div className="rmms-card p-4"><div className="overline">Under repair</div><div className="font-mono-data text-2xl mt-2 tabular-nums">{underRepair}</div></div>
        <div className="rmms-card p-4"><div className="overline">Open jobs</div><div className="font-mono-data text-2xl mt-2 tabular-nums">{openJobs.length}</div></div>
        <div className="rmms-card p-4"><div className="overline">Total jobs</div><div className="font-mono-data text-2xl mt-2 tabular-nums">{jobs.length}</div></div>
      </div>

      {/* Compliance documents */}
      <div className="rmms-card mb-6 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-heading font-semibold text-sm flex items-center gap-2"><Certificate size={14} /> Compliance documents</div>
          {canManageCompliance && (
            <button onClick={() => setComplianceModal({ entity_type: "store", entity_id: id, doc_type: "gas_safety" })} data-testid="store-compliance-add"
              className="text-xs inline-flex items-center gap-1 h-8 px-3 border border-slate-200 rounded-sm hover:bg-slate-50">
              <Plus size={11} weight="bold" /> Upload
            </button>
          )}
        </div>
        {storeDocs.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500" data-testid="store-compliance-empty">No compliance documents yet. Upload Gas Safety, Pest Control, Fire, EICR, Food Hygiene…</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {storeDocs.map(d => (
              <div key={d.id} data-testid={`store-compliance-row-${d.id}`} className="grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center px-5 py-3 hover:bg-slate-50">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{docTypes[d.doc_type] || d.doc_type}</div>
                  <div className="text-[11px] text-slate-500 truncate">{d.title} {d.issued_by && <>· {d.issued_by}</>}</div>
                </div>
                <div className="text-[12px] font-mono-data text-slate-600 hidden sm:block">{d.expiry_date ? `Expires ${fmtDateShort(d.expiry_date)}` : "No expiry"}</div>
                <ComplianceStatusBadge status={d.status} />
                <div className="flex gap-1">
                  {d.file_id && <button onClick={() => viewFile(d.file_id)} data-testid={`store-compliance-view-${d.id}`} className="h-7 px-2 text-[11px] inline-flex items-center gap-1 border border-slate-200 rounded-sm hover:bg-slate-50"><FilePdf size={11} weight="bold" /> View</button>}
                  {canManageCompliance && <button onClick={() => setComplianceModal({ ...d })} data-testid={`store-compliance-edit-${d.id}`} className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-slate-50"><Pencil size={11} /></button>}
                  {canDeleteCompliance && <button onClick={() => removeCompliance(d.id, d.title || d.doc_type)} data-testid={`store-compliance-delete-${d.id}`} className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-rose-50 hover:text-rose-600"><Trash size={11} /></button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Equipment list */}
        <div className="lg:col-span-2 rmms-card overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="font-heading font-semibold text-sm">Inventory</div>
            <Link to={`/equipment?store_id=${store.id}`} className="text-xs text-[#002FA7] hover:underline">View all →</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {equipment.length === 0 && <div className="px-5 py-12 text-center text-sm text-slate-500">No equipment registered yet.</div>}
            {equipment.map(e => (
              <Link to={`/equipment/${e.id}`} key={e.id} data-testid={`store-equipment-${e.id}`}
                className="grid grid-cols-[44px_1fr_auto] gap-3 items-center px-5 py-3 hover:bg-slate-50 transition-colors">
                <div className="h-11 w-11 rounded-sm border border-slate-200 overflow-hidden bg-slate-50">
                  {e.photos && e.photos.length > 0 ? <PhotoTile fileId={e.photos[0]} canRemove={false} /> : <div className="h-full w-full flex items-center justify-center text-slate-300"><Wrench size={20} weight="thin" /></div>}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{e.name}</div>
                  <div className="text-[11px] text-slate-500">{e.category}{e.location && <> · {e.location}</>}</div>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-heading uppercase tracking-wider font-semibold border rounded-sm ${EQ_STATUS_CLS[e.status] || EQ_STATUS_CLS.active}`}>{(e.status || "active").replace("_", " ")}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent jobs at this store */}
        <div className="rmms-card overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 font-heading font-semibold text-sm">Recent jobs</div>
          <div className="divide-y divide-slate-100">
            {jobs.length === 0 && <div className="px-5 py-12 text-center text-sm text-slate-500">No jobs yet.</div>}
            {jobs.slice(0, 8).map(j => (
              <Link to={`/jobs/${j.id}`} key={j.id} className="block px-5 py-3 hover:bg-slate-50">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono-data text-[11px] text-slate-500">{j.job_number}</span>
                  <PriorityBadge priority={j.priority} />
                </div>
                <div className="text-sm font-medium truncate">{j.title}</div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[11px] text-slate-500 font-mono-data">{fmtDate(j.created_at)}</span>
                  <StatusBadge status={j.status} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <ComplianceModal
        open={!!complianceModal}
        onClose={() => setComplianceModal(null)}
        presets={complianceModal || {}}
        onSaved={() => qc.invalidateQueries({ queryKey: ["compliance", "store", id] })}
      />
    </div>
  );
}
